/**
 * The stale-paragraph guard's Word-facing half: read the live paragraph, run the
 * shared identity predicate, and log the verdict.
 *
 * Extracted from AutoRedlinePreview.handleApplyAll so the decision can be tested
 * without a React tree, and so the component is left with the part that is
 * genuinely UI — setting the stale state and recording the attempt.
 */

import { AutoRedlineOutput } from "../../../interfaces/autoRedlineTypes";
import { normalizeParagraphText } from "../../../helpers/word";
import { createLogger } from "../../../utils/logger";
import { matchesExpectedParagraph, ParagraphMismatchReason } from "./paragraphIdentityGuard";
import { readLiveParagraphText } from "./wordSequentialEditService";

const log = createLogger("StaleParagraphCheck");

export interface StaleCheckVerdict {
  /** False means the apply must be blocked. */
  matched: boolean;
  /** True when a changed paragraph was accepted because WE changed it. */
  selfAttributed: boolean;
  reason?: ParagraphMismatchReason;
}

/**
 * Decide whether this preview's target paragraph still looks like the one the BE
 * generated against.
 *
 * The BE built its searchPhrase/occurrence coordinates from a snapshot of the
 * paragraph; if the user has edited it in Word since, those coordinates no longer
 * hold and applying could misplace the redline.
 *
 * Throws only if the Word read itself fails — the caller decides what to do with
 * that (today: proceed, and let a real failure surface through the apply path).
 */
export async function checkParagraphFreshness(
  redline: AutoRedlineOutput,
  messageId?: string
): Promise<StaleCheckVerdict> {
  const paragraphId = redline.paragraphId as number;
  const live = await readLiveParagraphText(paragraphId);

  const verdict = matchesExpectedParagraph({
    expectedText: redline.searchParagraph,
    liveText: live.text,
    outOfBounds: live.outOfBounds,
    operationType: redline.operationType ?? "edit",
    messageId,
    paragraphId,
  });

  // Lengths only — paragraph text is contract content and never goes to logs.
  const details = {
    sectionName: redline.sectionName,
    paragraphId,
    adjustedIndex: live.adjustedIndex,
    outOfBounds: live.outOfBounds,
    beLength: normalizeParagraphText(redline.searchParagraph ?? "").length,
    liveLength: normalizeParagraphText(live.text).length,
    selfAttributed: verdict.selfAttributed,
    matched: verdict.matched,
    reason: verdict.reason,
  };

  // Prod log level is WARN, so accepting a CHANGED paragraph is the branch worth
  // surfacing in the field — it is the one that relaxes the guard.
  if (verdict.selfAttributed) {
    log.warn("auto_redline stale check — self-attributed", details);
  } else {
    log.info("auto_redline stale check", details);
  }

  return verdict;
}
